import { useEffect, useState, useCallback } from "react";
import {
  Users,
  User,
  Phone,
  MessageSquare,
  Clock,
  Trash2,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  Brain,
} from "lucide-react";
import { channels } from "../../shared/constants";

const { ipcRenderer } = window.require("electron");

const STAGE_COLORS = {
  cold_lead: { dot: "bg-blue-400", border: "border-blue-500/30", bg: "from-blue-500/10 to-blue-600/5", text: "text-blue-400", badge: "bg-blue-500/20 text-blue-300" },
  warm_lead: { dot: "bg-yellow-400", border: "border-yellow-500/30", bg: "from-yellow-500/10 to-yellow-600/5", text: "text-yellow-400", badge: "bg-yellow-500/20 text-yellow-300" },
  hot_lead: { dot: "bg-red-400", border: "border-red-500/30", bg: "from-red-500/10 to-red-600/5", text: "text-red-400", badge: "bg-red-500/20 text-red-300" },
  purchased: { dot: "bg-green-400", border: "border-green-500/30", bg: "from-green-500/10 to-green-600/5", text: "text-green-400", badge: "bg-green-500/20 text-green-300" },
};

const LeadCard = ({ lead, stages, currentStageIndex, onMove, onRemove }) => {
  const phoneDisplay = lead.phoneNumber || lead.chatId?.split("@")[0] || "";
  const lastInteraction = lead.lastInteractionAt
    ? new Date(lead.lastInteractionAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })
    : "";

  const canMoveLeft = currentStageIndex > 0;
  const canMoveRight = currentStageIndex < stages.length - 1;

  return (
    <div className="bg-gradient-to-br from-slateSecondary/80 to-slatePrimary/60 border border-primaryColor/20 rounded-xl p-4 mb-3 hover:border-primaryColor/40 transition-all duration-200 group backdrop-blur-sm">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primaryColor/20 flex-shrink-0">
            <User className="w-4 h-4 text-primaryLight" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-textPrimary truncate">
              {lead.name || phoneDisplay}
            </p>
            <div className="flex items-center gap-1 text-xs text-textMuted">
              <Phone className="w-3 h-3" />
              <span className="truncate">{phoneDisplay}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 text-xs text-textSubtle flex-shrink-0">
          <Clock className="w-3 h-3" />
          <span>{lastInteraction}</span>
        </div>
      </div>

      {lead.lastMessage && (
        <div className="mb-2 px-2 py-1.5 bg-slatePrimary/50 rounded-lg">
          <p className="text-xs text-textMuted truncate">
            <MessageSquare className="w-3 h-3 inline mr-1" />
            {lead.lastMessage}
          </p>
        </div>
      )}

      {lead.classificationReason && (
        <div className="mb-2 flex items-center gap-1">
          <Brain className="w-3 h-3 text-dashboardAccent" />
          <span className="text-xs text-dashboardAccent">{lead.classificationReason}</span>
        </div>
      )}

      <div className="flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => canMoveLeft && onMove(lead.chatId, stages[currentStageIndex - 1])}
            disabled={!canMoveLeft}
            className="p-1.5 rounded-lg hover:bg-primaryColor/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Mover para estágio anterior"
          >
            <ChevronLeft className="w-4 h-4 text-primaryLight" />
          </button>
          <button
            type="button"
            onClick={() => canMoveRight && onMove(lead.chatId, stages[currentStageIndex + 1])}
            disabled={!canMoveRight}
            className="p-1.5 rounded-lg hover:bg-primaryColor/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Mover para próximo estágio"
          >
            <ChevronRight className="w-4 h-4 text-primaryLight" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => onRemove(lead.chatId)}
          className="p-1.5 rounded-lg hover:bg-red-500/20 transition-colors"
          title="Remover lead"
        >
          <Trash2 className="w-4 h-4 text-red-400" />
        </button>
      </div>
    </div>
  );
};

const KanbanColumn = ({ stage, label, leads, stages, stageIndex, onMove, onRemove }) => {
  const colors = STAGE_COLORS[stage] || STAGE_COLORS.cold_lead;

  return (
    <div className={`flex flex-col min-w-[260px] max-w-[320px] flex-1 rounded-2xl border ${colors.border} bg-gradient-to-b ${colors.bg} backdrop-blur-xl min-h-[180px]`}>
      <div className="px-4 py-3 border-b border-primaryColor/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${colors.dot}`} />
            <h3 className="text-sm font-bold text-textPrimary">{label}</h3>
          </div>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors.badge}`}>
            {leads.length}
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3 max-h-[500px] custom-scrollbar">
        {leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-textMuted">
            <Users className="w-8 h-8 mb-2 opacity-30" />
            <p className="text-sm">Nenhum cliente nesta etapa</p>
            <p className="text-xs text-textSubtle">Arraste cards para cá</p>
          </div>
        ) : (
          leads.map((lead) => (
            <LeadCard
              key={lead.chatId}
              lead={lead}
              stages={stages}
              currentStageIndex={stageIndex}
              onMove={onMove}
              onRemove={onRemove}
            />
          ))
        )}
      </div>
    </div>
  );
};

export function KanbanBoard({ instanceId }) {
  const [leadsData, setLeadsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadLeads = useCallback(async () => {
    if (!instanceId) return;
    try {
      setIsLoading(true);
      const result = await ipcRenderer.invoke(channels.CRM_GET_LEADS_BY_STAGE, instanceId);
      setLeadsData(result);
    } catch (error) {
      console.error("Erro ao carregar leads:", error);
    } finally {
      setIsLoading(false);
    }
  }, [instanceId]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const handleMove = useCallback(async (chatId, newStage) => {
    const result = await ipcRenderer.invoke(channels.CRM_MOVE_LEAD, instanceId, chatId, newStage);
    if (result.success) {
      loadLeads();
    }
  }, [instanceId, loadLeads]);

  const handleRemove = useCallback(async (chatId) => {
    if (!window.confirm("Remover este lead do funil? Esta ação não pode ser desfeita.")) {
      return;
    }
    const result = await ipcRenderer.invoke(channels.CRM_REMOVE_LEAD, instanceId, chatId);
    if (result.success) {
      loadLeads();
    }
  }, [instanceId, loadLeads]);

  if (!instanceId) {
    return (
      <div className="bg-gradient-to-r from-primaryColor/10 via-dashboardAccent/15 to-tealPrimary/10 border border-primaryColor/20 rounded-2xl p-8 text-center backdrop-blur-xl">
        <Users className="w-12 h-12 text-primaryColor/40 mx-auto mb-3" />
        <p className="text-textPrimary font-semibold text-lg mb-1">CRM Kanban</p>
        <p className="text-textMuted text-sm">
          Selecione uma instância específica no filtro acima para visualizar o funil de clientes.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-primaryColor animate-spin" />
      </div>
    );
  }

  const stages = leadsData?.stages || [];
  const labels = leadsData?.labels || {};
  const leads = leadsData?.leads || {};

  const totalLeads = stages.reduce((sum, stage) => sum + (leads[stage]?.length || 0), 0);

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-textPrimary">Funil de Clientes</h2>
          <p className="text-sm text-textMuted">
            {totalLeads} {totalLeads === 1 ? "cliente" : "clientes"} na instância {instanceId}
            {" — "}Classificação automática por IA
          </p>
        </div>
        <button
          type="button"
          onClick={loadLeads}
          className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-primaryColor/20 border border-primaryColor/30 text-primaryLight text-sm font-medium hover:bg-primaryColor/30 transition-all"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Atualizar
        </button>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4 min-h-[200px]">
        {stages.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-textMuted py-8">
            <p className="text-sm">Nenhum estágio disponível. Envie mensagens para começar a rastrear leads.</p>
          </div>
        ) : (
          stages.map((stage, index) => (
            <KanbanColumn
              key={stage}
              stage={stage}
              label={labels[stage] || stage}
              leads={leads[stage] || []}
              stages={stages}
              stageIndex={index}
              onMove={handleMove}
              onRemove={handleRemove}
            />
          ))
        )}
      </div>
    </div>
  );
}
